import React from 'react';
import { haptics } from '../../utils/haptics';

export type HapticName =
  | 'tap'
  | 'softTap'
  | 'notice'
  | 'boundary'
  | 'transition'
  | 'success'
  | 'rowNext'
  | 'rowPrev'
  | 'sessionPause';

export interface TouchableProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick'
> {
  haptic: HapticName | null;
  hapticOverride?: HapticName;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  ref?: React.Ref<HTMLButtonElement>;
}

// Keyboard/switch-control users need a visible focus indicator. Applied here on
// the base so every button variant gets it consistently (previously only the
// `lg` Primary/Soft variants had a ring). `focus-visible` keeps it keyboard-only
// so mouse/touch presses don't show a ring.
const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';

export const Touchable: React.FC<TouchableProps> = ({
  haptic,
  hapticOverride,
  onClick,
  type,
  disabled,
  children,
  ref,
  className,
  ...rest
}) => {
  const ariaDisabled = rest['aria-disabled'];
  const isAriaDisabled = ariaDisabled === true || ariaDisabled === 'true';

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !isAriaDisabled) {
      const name = hapticOverride ?? haptic;
      if (name) haptics[name]();
    }
    onClick?.(e);
  };

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled}
      onClick={handleClick}
      className={className ? `${FOCUS_RING} ${className}` : FOCUS_RING}
      {...rest}
    >
      {children}
    </button>
  );
};
